import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, Phone, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, SERVICE_LINKS, BUSINESS } from "../lib/constants";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileServices, setMobileServices] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropRef = useRef(null);

  useEffect(() => {
    setOpen(false);
    setDropOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isActive = (to) => location.pathname === to;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 backdrop-blur-xl bg-white/80 border-b ${
        scrolled ? "border-brand-line/70 shadow-[0_8px_30px_rgba(19,28,26,0.06)]" : "border-brand-line/40"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" aria-label="Kay's Carpet Cleaning — home" data-testid="nav-logo-link" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-7" aria-label="Primary">
          {NAV_LINKS.map((l) =>
            l.dropdown ? (
              <div key="services" className="relative" ref={dropRef}>
                <button
                  onClick={() => setDropOpen(!dropOpen)}
                  data-testid="nav-services-dropdown-toggle"
                  aria-expanded={dropOpen}
                  aria-haspopup="true"
                  className={`inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                    dropOpen ? "text-brand" : "text-brand-muted hover:text-brand"
                  }`}
                >
                  Services
                  <ChevronDown size={15} className={`transition-transform duration-300 ${dropOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {dropOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.98 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[22rem] bg-white border border-brand-line/70 rounded-2xl shadow-[0_24px_60px_rgba(19,28,26,0.14)] p-2.5 grid gap-0.5"
                      data-testid="nav-services-dropdown"
                    >
                      {SERVICE_LINKS.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          data-testid={`nav-dropdown-${s.to.slice(1)}`}
                          className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200 ${
                            s.label.includes("Water")
                              ? "text-brand-dark bg-brand-mist/70 hover:bg-brand-mist font-semibold"
                              : "text-brand-ink hover:bg-brand-bg hover:text-brand"
                          }`}
                        >
                          {s.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                  isActive(l.to) ? "text-brand" : "text-brand-muted hover:text-brand"
                }`}
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-2.5 shrink-0">
          <a
            href={BUSINESS.phoneHref}
            data-testid="nav-call-button"
            className="hidden xl:inline-flex items-center gap-2 text-sm font-semibold text-brand-ink hover:text-brand transition-colors duration-300 mr-1"
          >
            <Phone size={16} className="text-brand" />
            {BUSINESS.phoneDisplay}
          </a>
          <button
            onClick={() => navigate("/get-estimate")}
            data-testid="nav-get-estimate-button"
            className="group hidden sm:inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-semibold pl-5 pr-4 py-2.5 rounded-full transition-[background-color,transform] duration-300 hover:scale-[0.97]"
          >
            Get an Estimate
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            className="lg:hidden p-2 text-brand-ink"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-brand-line/60 max-h-[70vh] overflow-y-auto"
            aria-label="Mobile"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-5 flex flex-col">
              {NAV_LINKS.map((l) =>
                l.dropdown ? (
                  <div key="services" className="border-b border-brand-line/50">
                    <button
                      onClick={() => setMobileServices(!mobileServices)}
                      data-testid="mobile-services-toggle"
                      aria-expanded={mobileServices}
                      className="w-full flex items-center justify-between py-3.5 text-base font-medium text-brand-ink"
                    >
                      Services
                      <ChevronDown size={18} className={`transition-transform duration-300 ${mobileServices ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence initial={false}>
                      {mobileServices && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="pb-3 pl-3 flex flex-col">
                            {SERVICE_LINKS.map((s) => (
                              <Link
                                key={s.to}
                                to={s.to}
                                data-testid={`mobile-nav-${s.to.slice(1)}`}
                                className="py-2.5 text-sm text-brand-muted hover:text-brand"
                              >
                                {s.label}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="py-3.5 text-base font-medium text-brand-ink border-b border-brand-line/50"
                  >
                    {l.label}
                  </Link>
                )
              )}
              <div className="mt-5 grid grid-cols-2 gap-3 pb-2">
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="mobile-nav-call-button"
                  className="inline-flex items-center justify-center gap-2 border border-brand text-brand font-semibold px-4 py-3 rounded-full text-sm"
                >
                  <Phone size={15} /> Call Now
                </a>
                <Link
                  to="/get-estimate"
                  data-testid="mobile-nav-estimate-button"
                  className="inline-flex items-center justify-center gap-2 bg-brand text-white font-semibold px-4 py-3 rounded-full text-sm"
                >
                  Get an Estimate
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
